import { useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { useAuth } from '../../contexts/AuthContext';

// Whole-city building-footprint overlay, rendered server-side to PNG and shown
// via google.maps.ImageMapType - the same rock-solid mechanism the map already
// uses for the Esri imagery overlay. This replaced a deck.gl GoogleMapsOverlay
// attempt that would not render on this @vis.gl VECTOR map (WebGLOverlayView
// never drove deck). ImageMapType has zero WebGL/deck/CDN fragility: Google
// fetches, positions, caches and composites the tiles natively, so it works on
// any browser and behind a VPN.
//
// When a viewport holds more footprints than google.maps.Polygon can draw (a
// dense city like Qom zoomed out), the parent hides the editable Polygon layer
// and shows THIS overlay instead, so the operator still sees every building.

// Google-Maps zoom (256-tile) at which footprints become visible. Below this a
// single tile holds tens of thousands of sub-pixel buildings, so the server
// returns a transparent tile and the map shows the "zoom in" hint instead.
// Exported so the parent's overview-hint copy stays in sync.
export const MVT_MIN_ZOOM = 14;

export default function BuildingsRasterLayer({ visible }) {
  const map = useMap();
  const { session } = useAuth();
  const token = session?.access_token || null;

  useEffect(() => {
    if (!map || !visible || !token) return undefined;
    const g = window.google;
    if (!g || !g.maps) return undefined;

    // ImageMapType fetches tiles as plain image requests and cannot send an
    // Authorization header, so the access token rides in the query string. The
    // tile pixels carry no PII; RLS + the server-side approval gate remain the
    // trust boundary. The token is captured in this closure, so the effect
    // re-runs (rebuilding the layer) when the session refreshes.
    const layer = new g.maps.ImageMapType({
      name: 'buildings',
      tileSize: new g.maps.Size(256, 256),
      minZoom: MVT_MIN_ZOOM,
      maxZoom: 22,
      opacity: 1,
      getTileUrl: ({ x, y }, zoom) =>
        `/api/tiles/buildings/${zoom}/${x}/${y}.png?token=${encodeURIComponent(token)}`,
    });
    layer._isBuildings = true; // tag for safe removal (overlayMapTypes is shared with Esri)
    map.overlayMapTypes.push(layer);

    return () => {
      const ov = map.overlayMapTypes;
      for (let i = ov.getLength() - 1; i >= 0; i -= 1) {
        const t = ov.getAt(i);
        if (t && t._isBuildings) ov.removeAt(i);
      }
    };
  }, [map, visible, token]);

  return null;
}
